import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
} from '@mui/material';

/**
 * PivotCrossTab
 *
 * Renders a Year > Quarter × status-value cross-tab identical to the
 * Excel pivot layout:
 *
 *   Row Labels | New | Closed | Cancelled | Grand Total
 *   2023       |     |        |           |
 *     Qtr1     |     |        |           |
 *     ...
 *   Grand Total|     |        |           |
 *
 * Props:
 *   data        — raw flat array from the API:
 *                 [{ year, quarter, <statusKey>: value, count }, ...]
 *   statusKey   — the field name holding the status string
 *                 ('request_status' | 'approval_status')
 *   accentColor — hex string for header accent
 */
const PivotCrossTab = ({ data = [], statusKey = 'request_status', accentColor = '#003781' }) => {
  if (!data || data.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
        No data available
      </Typography>
    );
  }

  // Collect ordered unique values
  const years = [...new Set(data.map((r) => r.year))].sort();
  const statuses = [...new Set(data.map((r) => r[statusKey]))].sort();
  const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Build lookup: { year: { quarter: { status: count } } }
  const lookup = {};
  data.forEach(({ year, quarter, count, ...rest }) => {
    const status = rest[statusKey];
    if (!lookup[year]) lookup[year] = {};
    if (!lookup[year][quarter]) lookup[year][quarter] = {};
    lookup[year][quarter][status] = (lookup[year][quarter][status] || 0) + count;
  });

  // Helper: get count safely
  const get = (year, quarter, status) =>
    (lookup[year]?.[quarter]?.[status]) || 0;

  // Row total for a (year, quarter) pair
  const rowTotal = (year, quarter) =>
    statuses.reduce((s, st) => s + get(year, quarter, st), 0);

  // Year subtotal for a status
  const yearStatusTotal = (year, status) =>
    QUARTERS.reduce((s, q) => s + get(year, q, status), 0);

  // Year grand total
  const yearTotal = (year) =>
    statuses.reduce((s, st) => s + yearStatusTotal(year, st), 0);

  // Grand total per status
  const grandStatusTotal = (status) =>
    years.reduce((s, y) => s + yearStatusTotal(y, status), 0);

  // Overall grand total
  const grandTotal = statuses.reduce((s, st) => s + grandStatusTotal(st), 0);

  // Shared cell sx helpers
  const headerSx = {
    fontWeight: 700,
    fontSize: 12,
    color: '#fff',
    bgcolor: accentColor,
    borderColor: `${accentColor}80`,
    py: 1.25,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  };
  const yearRowSx = {
    fontWeight: 700,
    fontSize: 13,
    color: '#1a1a2e',
    bgcolor: '#eef1f8',
    borderBottom: `2px solid ${accentColor}30`,
    py: 1,
  };
  const qtrRowSx = {
    fontSize: 12,
    color: '#374151',
    bgcolor: '#fff',
    pl: 3,
    py: 0.9,
  };
  const grandRowSx = {
    fontWeight: 700,
    fontSize: 13,
    color: '#1a1a2e',
    bgcolor: '#dce3f0',
    borderTop: `2px solid ${accentColor}50`,
    py: 1,
  };
  const numSx = { textAlign: 'right', fontSize: 13, py: 0.9, borderColor: '#f0f1f3' };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, textAlign: 'left', minWidth: 140 }}>
              Row Labels
            </TableCell>
            {statuses.map((s) => (
              <TableCell key={s} sx={headerSx}>
                {s}
              </TableCell>
            ))}
            <TableCell sx={headerSx}>Grand Total</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {years.map((year) => {
            // Which quarters actually have data for this year?
            const activeQuarters = QUARTERS.filter((q) => rowTotal(year, q) > 0);
            return (
              <React.Fragment key={year}>
                {/* Year subtotal row */}
                <TableRow>
                  <TableCell sx={yearRowSx}>{year}</TableCell>
                  {statuses.map((s) => (
                    <TableCell key={s} sx={{ ...yearRowSx, ...numSx }}>
                      {yearStatusTotal(year, s) || '—'}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...yearRowSx, ...numSx }}>{yearTotal(year)}</TableCell>
                </TableRow>

                {/* Quarter rows */}
                {activeQuarters.map((q) => (
                  <TableRow key={`${year}-${q}`}>
                    <TableCell sx={qtrRowSx}>{q}</TableCell>
                    {statuses.map((s) => (
                      <TableCell key={s} sx={{ ...numSx, bgcolor: '#fff' }}>
                        {get(year, q, s) || '—'}
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...numSx, fontWeight: 600, bgcolor: '#fff' }}>
                      {rowTotal(year, q)}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}

          {/* Grand total row */}
          <TableRow>
            <TableCell sx={grandRowSx}>Grand Total</TableCell>
            {statuses.map((s) => (
              <TableCell key={s} sx={{ ...grandRowSx, ...numSx }}>
                {grandStatusTotal(s)}
              </TableCell>
            ))}
            <TableCell sx={{ ...grandRowSx, ...numSx }}>{grandTotal}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PivotCrossTab;
